import React, { useCallback, useContext, useState } from 'react';
import { Handle, Position, useEdges, useNodes, useReactFlow } from 'reactflow';
import { ExclamationTriangleIcon, Pencil1Icon } from '@radix-ui/react-icons';
import { BsBatteryCharging } from 'react-icons/bs';
import BatteryConfigModal from './BatteryConfigModal';
import { usePipeline } from '../../../context/PipelineContext';
import { NodeMappingContext } from '../../Canvas/Canvas';
import { notifyError } from '../../../errors/notifyError';
import './BatteryNode.css';

const BatteryNode = ({ id, data, selected }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isConfiguring, setIsConfiguring] = useState(false);
  const reactFlowInstance = useReactFlow();
  const edges = useEdges();
  const nodes = useNodes();
  const { session, addBatteryNode, updateSelectedNode } = usePipeline();
  const { updateNodeMapping, getNodeMapping } = useContext(NodeMappingContext);

  const {
    name,
    batteryHours,
    batteryCapacityKw,
    roundTripEfficiency,
    dod,
    batteryCapexPerKwh,
    batteryOmPerc,
    loanPerc,
    annualRate,
    tenureYears,
    batteryAssumptions,
    chargeTimeSlots,
    dischargeTimeSlots,
  } = data;

  const hasInputConnection = edges.some(edge => edge.target === id);
  const isConfigured = data.isConfigured || data.isApiConfigured || (!!name && !!batteryHours);

  const getInputNodeName = useCallback(() => {
    const inputEdge = edges.find(edge => edge.target === id);
    if (!inputEdge) return data.inputNodeName || null;
    const sourceNodeId = inputEdge.source;
    const sourceNode = nodes.find(node => node.id === sourceNodeId);
    if (sourceNode?.data?.name) return sourceNode.data.name;
    const mapping = getNodeMapping();
    return mapping[sourceNodeId] || sourceNodeId;
  }, [edges, id, nodes, getNodeMapping, data.inputNodeName]);

  const getNodePosition = useCallback(() => {
    const node = nodes.find(n => n.id === id);
    return node ? node.position : { x: 0, y: 0 };
  }, [nodes, id]);

  const openConfigModal = useCallback(() => {
    if (!hasInputConnection && !data.inputNodeName) {
      notifyError(new Error('Please connect a generation input to this battery node before configuring it.'), {
        toastId: 'battery.connectInput',
      });
      return;
    }
    setIsModalOpen(true);
  }, [hasInputConnection, data.inputNodeName]);

  const closeConfigModal = useCallback(() => setIsModalOpen(false), []);

  const handleConfigured = useCallback((configData) => {
    setIsConfiguring(true);
    try {
      const position = getNodePosition();
      const inputNodeName = configData.inputNodeName || getInputNodeName();
      updateNodeMapping(id, configData.name);
      addBatteryNode(configData.name, configData, inputNodeName, { x: position.x, y: position.y });
      reactFlowInstance.setNodes((currentNodes) =>
        currentNodes.map((node) => {
          if (node.id !== id) return node;
          return {
            ...node,
            data: {
              ...node.data,
              ...configData,
              inputNodeName,
              isConfigured: true,
              isApiConfigured: true,
            }
          };
        })
      );
      if (session.selected_node === id) {
        updateSelectedNode(configData.name);
      }
    } catch (error) {
      notifyError(error);
    } finally {
      setIsConfiguring(false);
      closeConfigModal();
    }
  }, [addBatteryNode, closeConfigModal, getInputNodeName, getNodePosition, id, reactFlowInstance, session.selected_node, updateNodeMapping, updateSelectedNode]);

  return (
    <>
      <div className={`battery-node ${selected ? 'battery-selected' : isConfigured ? 'battery-configured' : 'battery-not-configured'}`}>
        <div className={`battery-node-header ${isConfigured ? 'battery-configured' : 'battery-not-configured'}`}>
          <div className="battery-node-header-title">
            <div className="battery-node-type">Battery</div>
            {name && isConfigured && <div className="battery-node-name-display">{name}</div>}
          </div>
          <div className="battery-node-header-icons">
            <button
              onClick={openConfigModal}
              className="battery-config-button"
              title={isConfigured ? 'Edit Battery Node' : hasInputConnection ? 'Configure Battery Node' : 'Connect an input first'}
              disabled={isConfiguring}
            >
              {isConfigured ? <Pencil1Icon className="battery-icon" /> : <ExclamationTriangleIcon className="battery-icon" />}
            </button>
            <BsBatteryCharging className="battery-icon" />
          </div>
        </div>
        <div className="battery-node-content">
          <div className="battery-text-xs">
            {!hasInputConnection && !isConfigured && <div className="battery-text-warning">Connect generation input</div>}
            {hasInputConnection && !isConfigured && <div className="battery-text-warning">Parameters not configured</div>}
            {isConfiguring && <div className="battery-text-info">Configuring...</div>}
            {isConfigured && (
              <>
                <div>Hours: {batteryHours}</div>
                <div>Charge slots: {Array.isArray(chargeTimeSlots) ? chargeTimeSlots.length : 0}</div>
                <div>Discharge slots: {Array.isArray(dischargeTimeSlots) ? dischargeTimeSlots.length : 0}</div>
                {batteryCapacityKw !== undefined && batteryCapacityKw !== null && batteryCapacityKw !== '' && <div>Capacity: {batteryCapacityKw} kW</div>}
                <div>RTE: {roundTripEfficiency}%</div>
                <div>DoD: {dod}%</div>
                <div>CAPEX: {batteryCapexPerKwh}</div>
                <div>O&M: {batteryOmPerc}%</div>
                <div>Loan: {loanPerc}% at {annualRate}%</div>
                <div>Tenure: {tenureYears} years</div>
                <div>Yearwise model: {batteryAssumptions?.enabled ? 'Active' : 'Legacy'}</div>
              </>
            )}
          </div>
          <Handle
            type="target"
            position={Position.Left}
            id="input"
            className="battery-connection-point battery-input"
            isConnectable={!isConfigured}
          />
          <Handle
            type="source"
            position={Position.Right}
            id="output"
            className={`battery-connection-point ${isConfigured ? 'battery-configured-output' : 'battery-not-configured'}`}
            isConnectable={isConfigured}
            data-maxconnections="1"
          />
        </div>
      </div>

      <BatteryConfigModal
        isOpen={isModalOpen}
        onClose={closeConfigModal}
        onConfigured={handleConfigured}
        initialData={{
          ...data,
          inputNodeName: getInputNodeName(),
          position: getNodePosition(),
        }}
      />
    </>
  );
};

export default BatteryNode;
